/**
 * Reconciliation for edit-flow PhenoML re-extraction.
 *
 * When a provider re-runs extraction against an amended clinical summary on a
 * submitted order, the result must be merged with the phenotypes they have
 * already curated, not used to overwrite them. The merge is intentionally
 * additive only (a union):
 *
 *  - every existing term is preserved (manual curation and previously
 *    extracted terms alike), and
 *  - only HPO codes that are not already present are appended.
 *
 * Nothing is auto-removed, so a code dropped from the summary text still stays
 * in the list until the provider removes it explicitly. The richer
 * accept/dismiss diff-reconcile UI is tracked separately.
 */
import type { CategorizedHPOOption } from '@/types/RequisitionFormTypes'

/**
 * Normalize an HPO identifier to a stable `HP:NNNNNNN` comparison key so that
 * `HP_0001250`, `hp:0001250`, and a bare `0001250` all collide.
 */
export function normalizePhenotypeKey(value?: string): string {
  if (!value) {
    return ''
  }
  const trimmed = value.trim()
  if (!trimmed) {
    return ''
  }
  const underscoreMatch = trimmed.match(/HP_\d+/i)
  if (underscoreMatch) {
    return underscoreMatch[0].replace('_', ':').toUpperCase()
  }
  const colonMatch = trimmed.match(/HP:\d+/i)
  if (colonMatch) {
    return colonMatch[0].toUpperCase()
  }
  const digitsMatch = trimmed.match(/\b(\d{7})\b/)
  if (digitsMatch) {
    return `HP:${digitsMatch[1]}`
  }
  return trimmed.toUpperCase()
}

export interface PhenotypeMergeResult {
  /** Existing terms followed by any genuinely new extracted terms. */
  merged: CategorizedHPOOption[]
  /** The subset of `extracted` that was not already present in `existing`. */
  additions: CategorizedHPOOption[]
}

/**
 * Whether a term is eligible to be flagged as drifted when a re-extraction no
 * longer surfaces it. Only terms the provider explicitly curated by hand
 * (`source: 'manual'`) are exempt; everything else is treated as
 * automatically-extracted and therefore reconcilable.
 *
 * This is intentionally a denylist (not-`manual`) rather than an allowlist of
 * `phenoml`/`extracted`, because provenance is not round-tripped through the
 * backend: the persisted `clinicalDiagnosis` schema
 * (provider-api `ClinicalDiagnosisItem`) carries only id/node/label/category,
 * so a submitted order reopened for editing comes back with `source`
 * undefined. An allowlist would then never flag a drifted term on any
 * reopened order. Manual terms are the one case we can and must protect: the
 * Indications-modal save path tags genuinely provider-added terms `'manual'`.
 */
export function isAutoExtractedSource(source: CategorizedHPOOption['source']): boolean {
  return source !== 'manual'
}

/**
 * Union-merge re-extracted phenotypes into the provider's current set.
 * Existing terms always win on conflict; only codes absent from `existing`
 * are appended (and duplicates within `extracted` are collapsed).
 */
export function unionMergePhenotypes(
  existing: CategorizedHPOOption[],
  extracted: CategorizedHPOOption[]
): PhenotypeMergeResult {
  const seen = new Set(existing.map((term) => normalizePhenotypeKey(term.id)))
  const additions: CategorizedHPOOption[] = []

  for (const term of extracted) {
    const key = normalizePhenotypeKey(term.id)
    if (!key || seen.has(key)) {
      continue
    }
    seen.add(key)
    additions.push(term)
  }

  return { merged: [...existing, ...additions], additions }
}

/**
 * Identify previously auto-extracted phenotypes that the latest re-extraction
 * no longer surfaces, i.e. terms that have likely drifted out of the amended
 * clinical summary and are candidates for review/removal.
 *
 * Only terms considered auto-extracted (see `isAutoExtractedSource`: anything
 * not explicitly `source: 'manual'`) are eligible. Manually curated terms
 * always persist and are never flagged for removal. Nothing is removed here --
 * the result is purely advisory and the provider confirms each removal.
 */
export function computeDriftedPhenotypes(
  existing: CategorizedHPOOption[],
  extracted: CategorizedHPOOption[]
): CategorizedHPOOption[] {
  const extractedKeys = new Set(extracted.map((term) => normalizePhenotypeKey(term.id)).filter(Boolean))

  return existing.filter((term) => {
    if (!isAutoExtractedSource(term.source)) {
      return false
    }
    const key = normalizePhenotypeKey(term.id)
    return Boolean(key) && !extractedKeys.has(key)
  })
}

/**
 * Narrow a previously-computed drift list to the terms that are still genuinely
 * up for review against the current order: present in `current` AND still
 * auto-extracted (not `source: 'manual'`; see `isAutoExtractedSource`).
 *
 * This guards the case where the provider re-affirms a term through the
 * Indications modal: a term saved as manual (newly added by the provider) must
 * no longer be flagged for removal, and bulk removal must not delete it. Terms
 * the provider already removed simply fall out (absent from `current`).
 */
export function filterActiveDrift(
  drifted: CategorizedHPOOption[],
  current: CategorizedHPOOption[]
): CategorizedHPOOption[] {
  const extractedKeys = new Set(
    current
      .filter((term) => isAutoExtractedSource(term.source))
      .map((term) => normalizePhenotypeKey(term.id))
      .filter(Boolean)
  )
  return drifted.filter((term) => extractedKeys.has(normalizePhenotypeKey(term.id)))
}

/**
 * Remove a single phenotype (matched by normalized HPO key) from a term list.
 * Returns a new array; the input is not mutated.
 */
export function removePhenotypeByKey(terms: CategorizedHPOOption[], id: string): CategorizedHPOOption[] {
  const target = normalizePhenotypeKey(id)
  if (!target) {
    return terms
  }
  return terms.filter((term) => normalizePhenotypeKey(term.id) !== target)
}

/**
 * Narrow a previously-computed list of proposed additions to the ones that are
 * still genuinely new, i.e. not already present in the provider's current
 * indications. This guards the case where the provider manually added one of
 * the proposed terms (via the Indications modal) between re-extraction and
 * confirming: such a term must drop out of the "Added" review section.
 */
export function filterPendingAdditions(
  additions: CategorizedHPOOption[],
  current: CategorizedHPOOption[]
): CategorizedHPOOption[] {
  const currentKeys = new Set(current.map((term) => normalizePhenotypeKey(term.id)).filter(Boolean))
  return additions.filter((term) => {
    const key = normalizePhenotypeKey(term.id)
    return Boolean(key) && !currentKeys.has(key)
  })
}

/**
 * Apply staged edit-flow reconciliation decisions to the current indications.
 *
 * The provider's per-term choices collapse to a single rule: a term should end
 * up present (kept) or absent (removed) regardless of which review section it
 * came from. `additionsToKeep` are proposed new terms the provider chose to
 * keep (appended when not already present); `termsToRemove` are terms the
 * provider chose to drop (filtered out by normalized HPO key). Removal wins on
 * conflict. Returns a new array; inputs are not mutated.
 */
export function applyReconciliation(
  current: CategorizedHPOOption[],
  additionsToKeep: CategorizedHPOOption[],
  termsToRemove: CategorizedHPOOption[]
): CategorizedHPOOption[] {
  const removeKeys = new Set(termsToRemove.map((term) => normalizePhenotypeKey(term.id)).filter(Boolean))
  const base = current.filter((term) => !removeKeys.has(normalizePhenotypeKey(term.id)))

  const presentKeys = new Set(base.map((term) => normalizePhenotypeKey(term.id)).filter(Boolean))
  const appended: CategorizedHPOOption[] = []
  for (const term of additionsToKeep) {
    const key = normalizePhenotypeKey(term.id)
    if (!key || presentKeys.has(key) || removeKeys.has(key)) {
      continue
    }
    presentKeys.add(key)
    appended.push(term)
  }

  return [...base, ...appended]
}
